import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { onOutboxChange, flushOutbox } from "../lib/outbox.js";
import AddMenu from "./AddMenu.jsx";

const NAV = [
  { to: "/", label: "Home", icon: "🏠" },
  { to: "/crm", label: "Contacts", icon: "👥" },
  { to: "/add", label: "Add", icon: "➕" },
  { to: "/receipts", label: "Receipts", icon: "🧾" },
  { to: "/more", label: "More", icon: "⋯" },
];

// App chrome: brand header + bottom nav, shared by all signed-in pages.
export default function Shell({ title, back, children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  useEffect(() => onOutboxChange(setPending), []);

  return (
    <div className="flex min-h-full flex-col bg-slate-50">
      {user?.demo && (
        <div className="safe-top bg-amber-400 px-4 py-1.5 text-center text-xs font-bold text-amber-950">
          DEMO — sample data, no live connections
        </div>
      )}
      <header className="brand-gradient safe-top sticky top-0 z-10 px-5 pb-4 pt-4 text-white shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {back ? (
              <button onClick={() => navigate(-1)} className="rounded-lg bg-white/15 px-3 py-1.5 text-sm">
                ←
              </button>
            ) : (
              <img src="/icons/icon.svg" alt="" className="h-9 w-9 rounded-lg" />
            )}
            <div>
              <p className="text-xs text-white/70">Business Tracker{__BT_FLAVOR__ === "staging" ? " \u00b7 DEV" : ""}</p>
              <p className="text-base font-semibold">
                {title || `Hi${user?.display_name ? `, ${user.display_name}` : ""}`}
              </p>
            </div>
          </div>
          <button onClick={logout} className="rounded-lg bg-white/15 px-3 py-1.5 text-xs font-medium">
            Sign out
          </button>
        </div>
      </header>

      {pending > 0 && (
        <button
          onClick={() => flushOutbox()}
          className="flex w-full items-center justify-center gap-2 bg-amber-100 px-4 py-2 text-xs font-medium text-amber-800"
        >
          ⏳ {pending} {pending === 1 ? "entry" : "entries"} saved offline — will sync when you're back
          online (tap to retry)
        </button>
      )}

      <main className="flex-1 space-y-4 px-5 py-5 pb-24">{children}</main>

      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-10 grid grid-cols-5 border-t border-slate-200 bg-white">
        {NAV.map((item) => {
          // Add is a universal quick-add sheet, not a page — every other tab still navigates.
          if (item.to === "/add") {
            // The route still exists (expense form) — keep the you-are-here
            // highlight when the user is on it, like every other tab.
            const onAddPage = location.pathname === "/add";
            return (
              <button
                key={item.to}
                onClick={() => setAddMenuOpen(true)}
                className={`flex flex-col items-center gap-0.5 py-2 text-[11px] ${
                  onAddPage ? "font-semibold text-navy-700" : "text-slate-500"
                }`}
              >
                <span className="text-2xl">{item.icon}</span>
                {item.label}
              </button>
            );
          }
          const active = location.pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex flex-col items-center gap-0.5 py-2 text-[11px] ${
                active ? "font-semibold text-navy-700" : "text-slate-500"
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              {item.label}
            </Link>
          );
        })}
      </nav>

      {addMenuOpen && <AddMenu onClose={() => setAddMenuOpen(false)} />}
    </div>
  );
}
